import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import { CreateRefVariables, ReferenceMutationInput } from '@/types/reference'
import { referenceKeys } from '@/services/reference/reference.keys'
import { sanitizeReferenceInput } from '@/services/reference/reference.input'
import { getBugsnag } from '@/utils/bugsnag.client'
import {
  createReferencePropertyMtn,
  deleteReferencePropertyMtn,
  updateReferencePropertyMtn,
} from '@/services/graphql/reference'

const client = getAmplifyClient()

export const invalidateReferenceQueries = async (
  queryClient: Pick<ReturnType<typeof useQueryClient>, 'invalidateQueries'>,
  refetchType: 'none' | 'all' = 'none',
) => {
  await queryClient.invalidateQueries({
    queryKey: referenceKeys.all(),
    refetchType,
  })
}

export const optimisticallySyncReferenceQueries = (
  queryClient: Pick<
    ReturnType<typeof useQueryClient>,
    'getQueriesData' | 'setQueryData'
  >,
  variables: CreateRefVariables,
) => {
  patchReferenceQueries(queryClient, variables)
}

export const executeReferenceUpsert = (variables: CreateRefVariables) =>
  variables.isNew
    ? createReferenceProperty(variables.input)
    : updateReferenceProperty(variables.input)

export const applyReferenceMutationToItems = (
  items: ReferenceMutationInput[] | undefined,
  variables: CreateRefVariables,
): ReferenceMutationInput[] | undefined => {
  if (!items) return items

  const nextItem = sanitizeReferenceInput(variables.input)
  const existingIndex = items.findIndex((item) => item.id === nextItem.id)

  if (existingIndex >= 0) {
    return items.map((item) =>
      item.id === nextItem.id ? { ...item, ...nextItem } : item,
    )
  }

  if (variables.isNew) {
    return [...items, nextItem]
  }

  return items
}

const patchReferenceQueries = (
  queryClient: Pick<
    ReturnType<typeof useQueryClient>,
    'getQueriesData' | 'setQueryData'
  >,
  variables: CreateRefVariables,
) => {
  const matchingQueries = queryClient.getQueriesData<{
    items: ReferenceMutationInput[]
  }>({
    queryKey: referenceKeys.all(),
  })

  for (const [queryKey, oldData] of matchingQueries) {
    if (!oldData?.items) continue
    const nextItems = applyReferenceMutationToItems(oldData.items, variables)
    queryClient.setQueryData(queryKey, {
      ...oldData,
      items: nextItems,
    })
  }
}

export const useUpsertReference = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['createReference'],
    mutationFn: executeReferenceUpsert,
    onSuccess: (_data, variables) => {
      patchReferenceQueries(queryClient, variables)
    },
    onError: (error, variables) => {
      const graphQLErrors =
        error && typeof error === 'object' && 'errors' in error
          ? error.errors
          : undefined
      const errorTypes = Array.isArray(graphQLErrors)
        ? graphQLErrors.flatMap((item: unknown) =>
            item &&
            typeof item === 'object' &&
            'errorType' in item &&
            typeof item.errorType === 'string'
              ? [item.errorType]
              : [],
          )
        : []
      try {
        getBugsnag()?.notify(new Error('Reference save failed'), (event) => {
          event.addMetadata('referenceMutation', {
            operation: variables.isNew ? 'create' : 'update',
            referenceId: variables.input.id,
            errorTypes,
          })
        })
      } catch {
        // Reporting must not prevent canonical data recovery after a failed save.
      }
    },
    /*     onMutate: ({ isNew, input }) => {
      queryClient.cancelQueries({
        queryKey: referenceKeys.all(),
      })

      const previousValues = queryClient.getQueriesData<{
        items: ReferenceInput[]
        nextToken: string | null
      }>({
        queryKey: referenceKeys.all(),
      })
      previousValues.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, {
          items: isNew
            ? [...(data?.items ?? []), input]
            : (data?.items ?? []).map((d) =>
                d.id === input.id ? { ...d, ...input } : d,
              ),
          nextToken: data?.nextToken,
        })
      })
    }, */
    onSettled: (_data, error) =>
      invalidateReferenceQueries(queryClient, error ? 'all' : 'none'),
  })
}

export const useUpsertReferencesBatch = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['upsertReferencesBatch'],
    mutationFn: (variables: CreateRefVariables[]) =>
      executeReferenceBatch(
        variables.map((item) => () => executeReferenceUpsert(item)),
      ),
    onSuccess: (_data, variables) => {
      variables.forEach((item) => {
        patchReferenceQueries(queryClient, item)
      })
    },
    onSettled: () => invalidateReferenceQueries(queryClient, 'all'),
  })
}

export const useDeleteReference = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['deleteReference'],
    mutationFn: deleteReferenceProperty,
    onSettled: () => invalidateReferenceQueries(queryClient),
  })
}

export const useDeleteReferencesBatch = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['deleteReferencesBatch'],
    mutationFn: (input: Array<{ id: string }>) =>
      executeReferenceBatch(
        input.map((item) => () => deleteReferenceProperty(item)),
      ),
    onSettled: () => invalidateReferenceQueries(queryClient, 'all'),
  })
}

const executeReferenceBatch = async <T>(
  operations: Array<() => Promise<T>>,
): Promise<Awaited<T>[]> => {
  let hasRejected = false
  let firstError: unknown
  const outcomes = await Promise.all(
    operations.map(async (operation) => {
      try {
        return { status: 'fulfilled' as const, value: await operation() }
      } catch (error) {
        if (!hasRejected) {
          hasRejected = true
          firstError = error
        }
        return { status: 'rejected' as const }
      }
    }),
  )

  if (hasRejected) throw firstError

  return outcomes.flatMap((outcome) =>
    outcome.status === 'fulfilled' ? [outcome.value] : [],
  )
}

const createReferenceProperty = async (input: ReferenceMutationInput) => {
  return await client.graphql({
    query: createReferencePropertyMtn,
    variables: {
      input: sanitizeReferenceInput(input),
    },
  })
}

const updateReferenceProperty = async (input: ReferenceMutationInput) => {
  return await client.graphql({
    query: updateReferencePropertyMtn,
    variables: {
      input: sanitizeReferenceInput(input),
    },
  })
}

const deleteReferenceProperty = async ({ id }) => {
  return await client.graphql({
    query: deleteReferencePropertyMtn,
    variables: {
      input: { id },
    },
  })
}
